import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Download, Play, ShieldCheck, CheckCircle2, Building2, ArrowRight, X } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import { useSubdomain } from '../contexts/SubdomainContext';
import api from '../services/api';

/**
 * Public "Selling Process Info" page — shareable URL a seller can send to a
 * buyer (or vice versa) BEFORE either side starts. Explains the 10-step Trados
 * workflow.
 *
 * SECURITY: the company doing the introducing is derived from the SUBDOMAIN
 * (e.g. wave.trados.app -> "Wave Function Trading", looked up server-side via
 * /companies/by-subdomain/<sub>). The page does NOT accept arbitrary names
 * from URL query params — anyone could fake those.
 *
 * On www.trados.app (no tenant subdomain) the headline is generic.
 *
 * Print-to-PDF: window.print() + a print stylesheet (no extra deps).
 */

const BRAND_NAVY = '#1E2A35';
const BRAND_GOLD = '#F5A623';

const STEPS = [
  { n: 1,  title: 'LOI / Soft Offer',     desc: 'Buyer submits a Letter of Intent (or Soft Offer) — Trados auto-verifies the document for tampering, fake formats, and red flags.' },
  { n: 2,  title: 'NDA',                  desc: 'Both parties sign a Non-Disclosure Agreement so sensitive details (volumes, pricing, source) stay protected.' },
  { n: 3,  title: 'ICPO',                 desc: 'Buyer issues an Irrevocable Corporate Purchase Order — the formal commitment to buy at agreed terms.' },
  { n: 4,  title: 'POP',                  desc: 'Seller provides Proof of Product (cargo allocation, photos, mill / refinery certs). Verified before moving forward.' },
  { n: 5,  title: 'Draft SPA',            desc: 'Trados generates the Draft Sales and Purchase Agreement based on agreed terms. Both sides review.' },
  { n: 6,  title: 'Final SPA',            desc: 'Final Sales and Purchase Agreement signed by both parties — legally binding contract.' },
  { n: 7,  title: 'Payment Instrument',   desc: 'Buyer issues the payment instrument (SBLC / DLC / MT-103 / escrow). Trados verifies issuing bank + format.' },
  { n: 8,  title: 'Delivery',             desc: 'Cargo is shipped per Incoterm. Trados tracks documentation milestones (BL, SGS, COA, COO).' },
  { n: 9,  title: 'Delivery & Closing',   desc: 'Buyer confirms receipt + quality. Final delivery + closing documentation exchanged.' },
  { n: 10, title: 'Closing',              desc: 'Funds released to seller. Deal closed on Trados — both sides earn verified-deal credit toward higher tiers.' },
];

// Multi-language overview videos (same set as the homepage tabs).
const VIDEOS = [
  { lang: 'English',    flag: '🇺🇸', id: 'cf0-JmpR8po' },
  { lang: 'Spanish',    flag: '🇪🇸', id: 'pcnRm9rMxEg' },
  { lang: 'Portuguese', flag: '🇧🇷', id: '-MtBoJxdWH0' },
  { lang: 'Arabic',     flag: '🇸🇦', id: 'SViDXINY6Yw' },
  { lang: 'Chinese',    flag: '🇨🇳', id: 'J1CnqOvULD0' },
];

/**
 * StepCard — animates via `activeStep` progression.
 * - status = upcoming | active | done
 *   upcoming: muted, faded, transparent number
 *   active:   gold ring + pulse + scale, bold colors, slid in from below
 *   done:     checkmark-style done state, full color
 * - The connector line below the card fills from 0% to 100% as the next step
 *   becomes active.
 */
const StepCard = ({ step, status, reducedMotion, hideConnector }) => {
  const isActive = status === 'active';
  const isDone = status === 'done';
  const isUpcoming = status === 'upcoming';

  return (
    <div className="relative">
      <motion.div
        layout
        initial={false}
        animate={{
          opacity: isUpcoming ? 0.45 : 1,
          scale: isActive ? 1.02 : 1,
          y: 0,
        }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative flex gap-4 p-5 bg-white rounded-2xl border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-shadow"
        style={{
          borderColor: isActive ? BRAND_GOLD : isDone ? BRAND_NAVY : '#e2e8f0',
          boxShadow: isActive ? `0 8px 28px rgba(245, 166, 35, 0.18)` : undefined,
        }}
      >
        <div className="relative flex-shrink-0">
          <motion.div
            animate={{
              scale: isActive ? 1.1 : 1,
              backgroundColor: isUpcoming ? '#cbd5e1' : isActive ? BRAND_GOLD : BRAND_NAVY,
              color: isActive ? BRAND_NAVY : '#ffffff',
            }}
            transition={{ duration: 0.4 }}
            className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
          >
            {isDone ? '✓' : step.n}
          </motion.div>
          {isActive && !reducedMotion && (
            <motion.div
              initial={{ scale: 1, opacity: 0.6 }}
              animate={{ scale: 1.8, opacity: 0 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ background: BRAND_GOLD }}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold" style={{ color: BRAND_NAVY }}>{step.title}</h3>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{step.desc}</p>
        </div>
      </motion.div>

      {/* connector to next step — fills as next becomes active.
          - step 10: never has a connector (last card overall).
          - step 5: hidden on md+ (it's the last card in column 1, no card below
            on desktop), but visible on mobile where layout stacks 1->10. */}
      {step.n < STEPS.length && (
        <div
          className={`relative h-4 ml-[26px] w-0.5 bg-slate-200 print:hidden overflow-hidden ${hideConnector ? 'md:hidden' : ''}`}
          aria-hidden="true"
        >
          <motion.div
            initial={false}
            animate={{ height: isDone ? '100%' : '0%' }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="absolute inset-x-0 top-0"
            style={{ background: BRAND_NAVY }}
          />
        </div>
      )}
    </div>
  );
};

const VideoModal = ({ open, onClose, videos = VIDEOS, label = 'Trados overview video' }) => {
  const [tab, setTab] = useState(0);

  // reset to first tab when modal re-opens or video set changes
  useEffect(() => { if (open) setTab(0); }, [open, videos]);

  // close on Escape; lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  const current = videos[Math.min(tab, videos.length - 1)];
  const showTabs = videos.length > 1;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div
        className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          {showTabs ? (
            <div className="flex items-center gap-2 overflow-x-auto">
              {videos.map((v, i) => (
                <button
                  key={v.lang || v.id}
                  onClick={() => setTab(i)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    i === tab ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={i === tab ? { background: BRAND_NAVY } : undefined}
                >
                  {v.flag && <span aria-hidden="true">{v.flag}</span>} {v.lang}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm font-bold" style={{ color: BRAND_NAVY }}>{current.lang || label}</div>
          )}
          <button
            onClick={onClose}
            aria-label="Close video"
            className="ml-3 p-2 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative bg-black" style={{ paddingTop: '56.25%' }}>
          <iframe
            key={current.id /* force reload on tab change */}
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${current.id}?autoplay=1&rel=0&modestbranding=1`}
            title={`${label} — ${current.lang || ''}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

const PublicSellingProcess = () => {
  const { company, isSubdomain, subdomain, loading: subdomainLoading } = useSubdomain();
  // Step-progression animation always plays on this page (it IS the page's
  // primary visual). prefers-reduced-motion shortens timings but doesn't skip.
  const reduced = useReducedMotion();
  const stepMs = reduced ? 300 : 900;
  const startDelayMs = reduced ? 100 : 600;
  // 0 = before start, 1..10 = active step, > 10 = settled (all done view).
  const [activeStep, setActiveStep] = useState(0);
  const [seqId, setSeqId] = useState(0); // bump to restart
  const [videoOpen, setVideoOpen] = useState(false);
  const [ceoOpen, setCeoOpen] = useState(false);

  const stepsRef = useRef(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    document.title = 'How the Trados Process Works | Trados';
    document.body.setAttribute('data-page', 'selling-process');
    return () => document.body.removeAttribute('data-page');
  }, []);

  // Kick off the sequence (on Replay click). Initial play is driven by the
  // IntersectionObserver below — only fires when the steps section enters view.
  useEffect(() => {
    if (seqId === 0) return; // initial mount handled by observer
    setActiveStep(0);
    const start = setTimeout(() => setActiveStep(1), startDelayMs);
    return () => clearTimeout(start);
  }, [seqId, startDelayMs]);

  // Start the sequence the FIRST time the steps section scrolls into view.
  useEffect(() => {
    const el = stepsRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setTimeout(() => setActiveStep(1), startDelayMs);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasPlayedRef.current) {
        hasPlayedRef.current = true;
        setTimeout(() => setActiveStep(1), startDelayMs);
        io.disconnect();
      }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [startDelayMs]);

  // Advance step every stepMs; when past the last, settle to "all done".
  useEffect(() => {
    if (activeStep < 1 || activeStep > STEPS.length) return;
    const t = setTimeout(() => setActiveStep((s) => s + 1), stepMs);
    return () => clearTimeout(t);
  }, [activeStep, stepMs]);

  const settled = activeStep > STEPS.length;
  const statusFor = (n) => {
    if (settled) return 'done';
    if (n < activeStep) return 'done';
    if (n === activeStep) return 'active';
    return 'upcoming';
  };
  const progressPct = Math.min(100, Math.round((Math.min(activeStep, STEPS.length) / STEPS.length) * 100));

  // Subdomain is the AUTHORITATIVE business identifier (security). On the main
  // domain (no subdomain), the headline stays generic.
  const introCompanyName = isSubdomain && company?.name ? company.name : null;

  // Referral / invite tracking — same params the InviteUsers page bakes into
  // its shareable links + that RegisterPage reads on submit. Passing these
  // through every CTA on this page (signup, login, the PDF URL) means the
  // inviter gets credit when the recipient signs up.
  const [searchParams] = useSearchParams();
  // Short links (/selling/wave, /selling/wave/7f3k2q) carry the same tracking
  // pair in the path instead of the query string; resolve them to ref_user /
  // ref_company so everything downstream is unchanged.
  const { ref: pathRef, code: pathCode } = useParams();
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!pathRef) return;
    let cancelled = false;
    const path = pathCode
      ? `/public/selling/${encodeURIComponent(pathRef)}/${encodeURIComponent(pathCode)}`
      : `/public/selling/${encodeURIComponent(pathRef)}`;
    api.get(path)
      .then((r) => { if (!cancelled) setResolved(r.data); })
      .catch(() => { if (!cancelled) setResolved(null); });
    return () => { cancelled = true; };
  }, [pathRef, pathCode]);

  const refUser = resolved?.ref_user || searchParams.get('ref_user') || '';
  const refCompany = resolved?.ref_company || searchParams.get('ref_company') || '';

  const trackingQs = useMemo(() => {
    const qs = new URLSearchParams();
    if (refUser) qs.set('ref_user', refUser);
    if (refCompany) qs.set('ref_company', refCompany);
    return qs.toString();
  }, [refUser, refCompany]);

  const buildRegisterHref = (path) => {
    const qs = new URLSearchParams({ next: '/admin/dashboard' });
    if (isSubdomain && subdomain) qs.set('ref', subdomain);
    if (refUser) qs.set('ref_user', refUser);
    if (refCompany) qs.set('ref_company', refCompany);
    return `${path}?${qs.toString()}`;
  };
  const signupHref = useMemo(() => buildRegisterHref('/register'), [isSubdomain, subdomain, refUser, refCompany]);
  const companyRegisterHref = useMemo(() => buildRegisterHref('/register/company'), [isSubdomain, subdomain, refUser, refCompany]);

  const loginHref = trackingQs ? `/login?${trackingQs}` : '/login';
  // The PDF prints a URL string. If we have tracking params, encode them so a
  // recipient who scans/copies it still attributes back to the inviter.
  // PDF: point at the company registration path (this page's audience is
  // typically company principals). Individual signup path is still on the page.
  const printedRegisterUrl = trackingQs
    ? `trados.app/register/company?${trackingQs}`
    : 'trados.app/register/company';

  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <PublicLayout>
      <style>{`
        /* PRINT / PDF — designed layout, keeps brand colors.
           Browsers strip backgrounds in print by default; print-color-adjust:exact
           forces the gold/navy palette through to the PDF. */
        @media print {
          @page { size: Letter; margin: 0.5in 0.55in 0.7in; }
          html, body {
            background: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .screen-only { display: none !important; }
          .print-only { display: block !important; }
          a { color: ${BRAND_NAVY} !important; text-decoration: none !important; }
          .step-card-print { page-break-inside: avoid !important; break-inside: avoid !important; }
          /* let hero/CTA backgrounds print as designed */
          .keep-bg { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .pdf-hero {
            background: ${BRAND_NAVY} !important; color: #ffffff !important;
            border-radius: 14px !important;
            padding: 28px 32px !important;
            margin-bottom: 24px !important;
          }
          .pdf-hero h1 { font-size: 26px !important; line-height: 1.2 !important; margin: 0 0 10px !important; }
          .pdf-hero .pdf-intro { font-size: 12px !important; line-height: 1.55 !important; color: rgba(255,255,255,0.85) !important; margin: 0 !important; }
          .pdf-hero .pdf-eyebrow {
            display: inline-block !important;
            font-size: 10px !important; font-weight: 800 !important; letter-spacing: 2px !important; text-transform: uppercase !important;
            color: ${BRAND_GOLD} !important;
            border: 1px solid ${BRAND_GOLD} !important;
            border-radius: 999px !important; padding: 3px 10px !important; margin-bottom: 12px !important;
          }
          /* 2-column grid in the PDF; column-major so step order = 1..5 down
             the left column, 6..10 down the right column (matches the screen). */
          .pdf-steps-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            grid-template-rows: repeat(5, auto) !important;
            grid-auto-flow: column !important;
            column-gap: 10px !important;
            row-gap: 0 !important;
          }
          .pdf-step {
            display: flex !important; gap: 10px !important;
            padding: 10px 12px !important;
            margin: 0 0 8px !important;
            background: #ffffff !important;
            border-left: 4px solid ${BRAND_GOLD} !important;
            border-top: 1px solid #e2e8f0 !important;
            border-right: 1px solid #e2e8f0 !important;
            border-bottom: 1px solid #e2e8f0 !important;
            border-radius: 8px !important;
          }
          .pdf-step .pdf-num {
            flex-shrink: 0 !important;
            width: 28px !important; height: 28px !important;
            border-radius: 50% !important;
            background: ${BRAND_NAVY} !important; color: #ffffff !important;
            display: flex !important; align-items: center !important; justify-content: center !important;
            font-weight: 800 !important; font-size: 12px !important;
          }
          .pdf-step h3 { font-size: 11.5px !important; font-weight: 800 !important; margin: 0 0 2px !important; color: ${BRAND_NAVY} !important; }
          .pdf-step p { font-size: 9.5px !important; line-height: 1.4 !important; color: #475569 !important; margin: 0 !important; }
          .pdf-cta {
            margin-top: 18px !important;
            background: #FFFDF5 !important;
            border: 1px solid ${BRAND_GOLD} !important;
            border-radius: 12px !important;
            padding: 18px 22px !important;
            text-align: center !important;
          }
          .pdf-cta h2 { font-size: 16px !important; font-weight: 800 !important; margin: 0 0 6px !important; color: ${BRAND_NAVY} !important; }
          .pdf-cta p { font-size: 11px !important; line-height: 1.5 !important; color: #475569 !important; margin: 0 !important; }
          .pdf-cta .pdf-url { display: inline-block !important; margin-top: 8px !important; font-size: 12px !important; font-weight: 700 !important; color: ${BRAND_NAVY} !important; border-bottom: 2px solid ${BRAND_GOLD} !important; padding-bottom: 2px !important; }
          .pdf-contact {
            margin-top: 16px !important;
            background: #ffffff !important;
            border: 1px solid #e2e8f0 !important;
            border-left: 4px solid ${BRAND_NAVY} !important;
            border-radius: 8px !important;
            padding: 14px 18px !important;
            page-break-inside: avoid !important;
          }
          .pdf-contact .pdf-contact-eyebrow { font-size: 9px !important; font-weight: 800 !important; letter-spacing: 1.5px !important; text-transform: uppercase !important; color: ${BRAND_GOLD} !important; margin: 0 0 4px !important; }
          .pdf-contact .pdf-contact-name { font-size: 15px !important; font-weight: 800 !important; color: ${BRAND_NAVY} !important; margin: 0 0 6px !important; }
          .pdf-contact .pdf-contact-line { font-size: 11px !important; color: #334155 !important; margin: 1px 0 !important; }
          .pdf-footer {
            margin-top: 16px !important;
            background: ${BRAND_NAVY} !important;
            border-top: 3px solid ${BRAND_GOLD} !important;
            color: #ffffff !important;
            padding: 10px 14px !important;
            border-radius: 0 0 8px 8px !important;
            font-size: 10px !important;
            display: flex !important; justify-content: space-between !important; align-items: center !important;
          }
          .pdf-footer span { color: #ffffff !important; }
          .pdf-header {
            display: flex !important; justify-content: space-between !important; align-items: center !important;
            padding-bottom: 10px !important; margin-bottom: 16px !important;
            border-bottom: 2px solid ${BRAND_GOLD} !important;
          }
          .pdf-header .brand { font-size: 16px !important; font-weight: 900 !important; letter-spacing: 1px !important; color: ${BRAND_NAVY} !important; }
          .pdf-header .meta { font-size: 10px !important; color: #64748b !important; text-align: right !important; }
        }
        @media screen { .print-only { display: none !important; } }

        /* PAGE FOOTER OVERRIDE — this is a Trados-branded info page rendered on
           tenant subdomains. Tenants can set their own external_footer_bg
           (wave.trados.app is a washed-out blue-gray #a8bccc), which clashes
           with the navy CTA + brand palette. Force a clean white footer with
           a gold accent here. Scoped via body[data-page="selling-process"]. */
        body[data-page="selling-process"] > footer,
        body[data-page="selling-process"] > div > footer,
        body[data-page="selling-process"] footer:last-of-type {
          background-color: #ffffff !important;
          border-top: 3px solid ${BRAND_GOLD} !important;
        }
        body[data-page="selling-process"] footer:last-of-type *:not(a) {
          color: #475569 !important;
        }
        /* Hide the WP header/footer chrome + any sticky chat / support widgets when printing. */
        @media print {
          .wp-chrome, .wp-chrome-header, .wp-chrome-footer,
          [data-testid="wp-chrome-header"], [data-testid="wp-chrome-footer"],
          .trados-chat-btn, [class*="chat-btn"], [class*="chat-widget"],
          [class*="support-widget"], [aria-label*="support" i],
          img[src*="Brandon" i], img[alt*="Brandon" i] {
            display: none !important; visibility: hidden !important;
          }
        }
      `}</style>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
      <VideoModal
        open={ceoOpen}
        onClose={() => setCeoOpen(false)}
        label="Message from the CEO"
        videos={[{ id: 'jcL8FV-oEok', lang: 'Message from Fabio DeMelo, Founder & CEO' }]}
      />

      {/* PRINT-ONLY layout — runs alongside the screen page; hidden on screen,
          visible (and the only thing visible) in print/PDF. */}
      <div className="print-only" aria-hidden="true">
        <div className="pdf-header">
          <div className="brand">TRADOS<span style={{ color: BRAND_GOLD }}>.</span></div>
          <div className="meta">
            The Trusted Trade Process<br/>
            {today}
          </div>
        </div>

        <div className="pdf-hero">
          <span className="pdf-eyebrow">The Trusted Trade Process</span>
          <h1>
            {introCompanyName
              ? <><span style={{ color: BRAND_GOLD }}>{introCompanyName}</span> has chosen to conduct business through Trados.</>
              : <>Conduct your next deal through Trados.</>}
          </h1>
          <p className="pdf-intro">
            Trados protects buyers <strong>and</strong> sellers — so neither side wastes time
            with fake counterparties, fraudulent LOIs, or unverified documents. Below is the
            full 10-step process every verified deal follows on our platform.
          </p>
        </div>

        <div className="pdf-steps-grid">
          {STEPS.map((s) => (
            <div key={`pdf-${s.n}`} className="pdf-step step-card-print">
              <div className="pdf-num">{s.n}</div>
              <div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {isSubdomain && company?.contact && (company.contact.name || company.contact.email || company.contact.phone) && (
          <div className="pdf-contact">
            <p className="pdf-contact-eyebrow">Your contact at {company.name}</p>
            {company.contact.name && <p className="pdf-contact-name">{company.contact.name}</p>}
            {company.contact.email && <p className="pdf-contact-line">{company.contact.email}</p>}
            {company.contact.phone && <p className="pdf-contact-line">{company.contact.phone}</p>}
            {company.contact.country && <p className="pdf-contact-line">{company.contact.country}</p>}
          </div>
        )}

        <div className="pdf-cta">
          <h2>Start your next deal the safe way.</h2>
          <p>
            About 20 minutes to set up your account — and it can save thousands of dollars in
            fraud + weeks of wasted time on unverified counterparties.
          </p>
          <span className="pdf-url">{printedRegisterUrl}</span>
        </div>

        <div className="pdf-footer">
          <span>© {new Date().getFullYear()} Trados — The Trusted Trade Platform</span>
          <span>trados.app/requests/info</span>
        </div>
      </div>

      <main className="bg-slate-50 min-h-screen screen-only">
        {/* PAGE TITLE BAND — white space between WP header and navy hero */}
        <section className="bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-6 py-5 md:py-6">
            <h1 className="text-2xl md:text-4xl font-extrabold leading-tight" style={{ color: BRAND_NAVY }}>
              {introCompanyName ? <><span style={{ color: BRAND_GOLD }}>{introCompanyName}</span> Selling / Purchase Process</>
                                : <>Selling / Purchase Process</>}
            </h1>
          </div>
        </section>

        {/* HERO */}
        <section className="relative overflow-hidden" style={{ background: BRAND_NAVY }}>
          <div className="max-w-5xl mx-auto px-6 text-white" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'rgba(245,166,35,0.15)', color: BRAND_GOLD }}>
              <ShieldCheck className="w-3.5 h-3.5" /> The Trusted Trade Process
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              {subdomainLoading
                ? <>Conduct your next deal through <span style={{ color: BRAND_GOLD }}>Trados</span>.</>
                : introCompanyName
                  ? <><span style={{ color: BRAND_GOLD }}>{introCompanyName}</span> has chosen to conduct business through Trados.</>
                  : <>Conduct your next deal through <span style={{ color: BRAND_GOLD }}>Trados</span>.</>}
            </h1>
            <p className="text-base md:text-lg text-white/80 mt-5 max-w-3xl leading-relaxed">
              Trados protects buyers <strong>and</strong> sellers — so neither side wastes time
              dealing with fake counterparties, fraudulent LOIs, or unverified documents.
              Below is the full 10-step process every verified deal follows on our platform.
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-5 print:hidden">
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors"
                style={{ background: BRAND_GOLD, color: BRAND_NAVY }}
              >
                <Play className="w-4 h-4" /> Watch Trados overview
              </button>
              <button
                type="button"
                onClick={() => setCeoOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors text-white"
                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)' }}
              >
                <Play className="w-4 h-4" /> Watch CEO Video
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section ref={stepsRef} className="max-w-6xl mx-auto px-6 pt-8 pb-6 md:pt-10 md:pb-8">
          <div className="flex items-end justify-between gap-4 mb-6 print:hidden">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: BRAND_GOLD }}>The Process</p>
              <h2 className="text-2xl md:text-3xl font-extrabold" style={{ color: BRAND_NAVY }}>10 verified steps, every time</h2>
            </div>
            <button
              onClick={() => setSeqId((n) => n + 1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100"
              title="Replay step-by-step animation"
            >
              ↻ Replay animation
            </button>
          </div>

          {/* progress bar (hidden in print) */}
          <div className="mb-6 print:hidden">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1.5">
                <span>Deal progress</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="h-full"
                  style={{ background: `linear-gradient(to right, ${BRAND_NAVY}, ${BRAND_GOLD})` }}
                />
              </div>
            </div>

          {/* 2-column layout on desktop: steps 1-5 left, 6-10 right.
              md:grid-rows-5 + md:grid-flow-col gives column-major order so the
              cards flow top-to-bottom THEN left-to-right. Single column on mobile. */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-5 md:grid-flow-col md:gap-x-8 gap-y-0" key={seqId}>
            {STEPS.map((s) => (
              <StepCard
                key={s.n}
                step={s}
                status={statusFor(s.n)}
                reducedMotion={reduced}
                hideConnector={s.n === 5 || s.n === STEPS.length}
              />
            ))}
          </div>
        </section>

        {/* CONTACT — only on tenant subdomains (we have a real company to point to) */}
        {isSubdomain && company?.contact && (company.contact.name || company.contact.email || company.contact.phone) && (
          <section className="max-w-6xl mx-auto px-6 pb-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: BRAND_GOLD }}>
                  Your contact at {company.name}
                </p>
                {company.contact.name && (
                  <p className="text-2xl md:text-3xl font-extrabold" style={{ color: BRAND_NAVY }}>
                    {company.contact.name}
                  </p>
                )}
              </div>
              <div className="text-sm md:text-base text-slate-700 space-y-1.5 md:text-right">
                {company.contact.email && (
                  <div><a href={`mailto:${company.contact.email}`} className="font-semibold hover:underline" style={{ color: BRAND_NAVY }}>{company.contact.email}</a></div>
                )}
                {company.contact.phone && (
                  <div><a href={`tel:${company.contact.phone.replace(/[^+\d]/g,'')}`} className="hover:underline">{company.contact.phone}</a></div>
                )}
                {company.contact.country && <div className="text-slate-500">{company.contact.country}</div>}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-white border-t border-slate-200 print:hidden">
          <div className="max-w-4xl mx-auto px-6 py-8 md:py-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'rgba(245,166,35,0.15)', color: '#a06800' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> 20 minutes to set up — saves thousands in fraud
            </div>
            <h2 className="text-2xl md:text-4xl font-extrabold leading-tight" style={{ color: BRAND_NAVY }}>
              Ready to start your deal the safe way?
            </h2>
            <p className="text-base md:text-lg text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              Create your free Trados account and register your business — about 20 minutes —
              and you'll save thousands of dollars in potential fraud and weeks of wasted time
              chasing unverified counterparties.
            </p>

            {/* Single CTA — company registration only. This page is about
                deals between COMPANIES, so the only signup path we surface is
                the company one. Preserves ref_user/ref_company tracking. */}
            <div className="flex justify-center mt-8">
              <a
                href={companyRegisterHref}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg text-base font-bold transition-colors"
                style={{ background: BRAND_GOLD, color: BRAND_NAVY }}
              >
                <Building2 className="w-5 h-5" /> Register your company <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <p className="text-xs text-slate-400 mt-6">
              Already have an account?{' '}
              <Link to={loginHref} className="font-bold underline" style={{ color: BRAND_GOLD }}>Sign in</Link>
            </p>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
};

export default PublicSellingProcess;
